/**
 * Proposal Comment Service
 *
 * Story BL-024.2: Governance-Suite Comment Thread UI
 * Task 2: Create proposalCommentService
 *
 * REST client for the oracle-bridge proposal comments API.
 * All write operations use `credentials: 'include'` for cookie-based auth.
 *
 * CRITICAL: Uses `getOracleBridgeUrl()` NOT `getApiBaseUrl()` — see Dev Notes.
 *
 * @module services/proposalCommentService
 */

import { authenticatedFetch } from '@hello-world-dao/auth';
import { getOracleBridgeUrl } from './apiHelpers';
import type { Comment, CommentThread, VoteCounts } from '../types/proposalComments';

// =============================================================================
// Error Handling
// =============================================================================

export interface CommentApiError {
  code: 'UNAUTHENTICATED' | 'FORBIDDEN' | 'SERVER_ERROR';
  status?: number;
}

async function mapError(res: Response): Promise<CommentApiError> {
  if (res.status === 401) return { code: 'UNAUTHENTICATED' };
  if (res.status === 403) return { code: 'FORBIDDEN' };
  return { code: 'SERVER_ERROR', status: res.status };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get the top-level non-deleted comment count for a proposal (public endpoint).
 * Fails silently, returning 0 on any error (badge is non-critical UI).
 *
 * BL-024.3 AC2/AC3: Comment count badge support.
 */
export async function getCommentCount(proposalId: number): Promise<number> {
  try {
    const base = getOracleBridgeUrl();
    const res = await authenticatedFetch(
      `${base}/api/proposals/${proposalId}/comments/count`,
    );
    if (!res.ok) return 0;
    const data = (await res.json()) as { count: number };
    return data.count;
  } catch {
    return 0;
  }
}

/**
 * Batched comment counts for many proposals in a single request (BL-460).
 *
 * Backend: GET /api/proposals/comments/counts?ids=1,2,3,... — one SQL
 * round-trip on the oracle-bridge side. Replaces the N-fanout pattern
 * that was hammering the Postgres pool under shard-parallel test load
 * (240 concurrent fetches per nightly cycle, manifesting as bimodal
 * latency cluster on staging — see BL-460 staging trace).
 *
 * Fails silently with `{}` on any error (badges are non-critical UI).
 * Caller is responsible for the 100-ID per-request cap; the server
 * returns 400 above that limit. This client splits oversize inputs
 * into 100-ID chunks transparently.
 */
export async function getCommentCounts(
  proposalIds: number[],
): Promise<Record<number, number>> {
  const result: Record<number, number> = {};
  if (proposalIds.length === 0) return result;

  // Dedupe upfront so the URL stays short and we don't blow the cap on
  // accidental duplicates from the caller.
  const unique = Array.from(new Set(proposalIds));
  const CHUNK_SIZE = 100;
  const base = getOracleBridgeUrl();

  for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
    const chunk = unique.slice(i, i + CHUNK_SIZE);
    try {
      const res = await authenticatedFetch(
        `${base}/api/proposals/comments/counts?ids=${chunk.join(',')}`,
      );
      if (!res.ok) {
        // Surface degraded backend in observability without throwing —
        // badges are non-critical UI but silent failure is harder to
        // debug when it shows up in the wild.
        console.warn(
          `[proposalCommentService.getCommentCounts] chunk ${i}-${i + chunk.length} failed: HTTP ${res.status}`,
        );
        continue;
      }
      const data = (await res.json()) as unknown;
      // Defensive: validate the response shape before iterating. A
      // malformed server (e.g. {counts: "oops"} or {counts: 5}) would
      // otherwise either iterate string char-codes or throw on
      // Object.entries(number).
      const counts =
        data && typeof data === 'object' && 'counts' in data
          ? (data as { counts: unknown }).counts
          : null;
      if (!counts || typeof counts !== 'object' || Array.isArray(counts)) continue;
      for (const [id, rawCount] of Object.entries(counts as Record<string, unknown>)) {
        const n = Number(id);
        const count = Number(rawCount);
        if (Number.isFinite(n) && Number.isFinite(count)) result[n] = count;
      }
    } catch {
      // Fall through — one bad chunk shouldn't drop the others.
    }
  }
  return result;
}

/**
 * List comments for a proposal (public endpoint).
 */
export async function listComments(
  proposalId: number,
  sort: 'created_asc' | 'score_desc' = 'created_asc',
): Promise<{ comments: CommentThread[]; total_top_level: number }> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/proposals/${proposalId}/comments?sort=${sort}`,
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Post a new comment or reply (authenticated).
 */
export async function postComment(
  proposalId: number,
  content: string,
  parentId?: string,
): Promise<Comment> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(`${base}/api/proposals/${proposalId}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, ...(parentId ? { parentId } : {}) }),
  });
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Edit an existing comment (authenticated, author only).
 */
export async function editComment(
  proposalId: number,
  commentId: string,
  content: string,
): Promise<Comment> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/proposals/${proposalId}/comments/${commentId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Delete a comment (authenticated, author or admin/moderator).
 */
export async function deleteComment(
  proposalId: number,
  commentId: string,
): Promise<void> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/proposals/${proposalId}/comments/${commentId}`,
    {
      method: 'DELETE',
    },
  );
  if (!res.ok) throw await mapError(res);
}

/**
 * Vote on a comment (authenticated).
 */
export async function voteComment(
  proposalId: number,
  commentId: string,
  vote: 1 | -1,
): Promise<VoteCounts> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/proposals/${proposalId}/comments/${commentId}/vote`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vote }),
    },
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}
